import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { CHARACTER_LENGTH } from "@constants/index";
import { CustomTextarea } from "@components/Forms";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import useEscapeKey from "@hooks/useEscapeKey";

const AddImportComponentModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  validationError = null,
  onClearValidationError,
  "data-testid": dataTestId = "add-import-component-modal",
}) => {
    const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
    const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
    const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
    const fieldErrorTestId = (name) => `${dataTestId}-field-${name}-error`;
    const { colors } = useTheme();
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        component: "",
        componentVersion: "",
        componentGroup: "",
        importFileDownloadUrl: "",
        remark: ""
    });
    const prevIsOpenRef = useRef(false);

    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            setFormData({
                component: "",
                componentVersion: "",
                componentGroup: "",
                importFileDownloadUrl: "",
                remark: ""
            });
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    // Check if all required fields are filled
    const isFormValid = formData.importFileDownloadUrl.trim();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid || isLoading) {
            console.error("Form validation failed or already loading");
            return;
        }

        if (onSubmit) {
            await onSubmit(formData);
        }
    };

    const handleClose = () => {
        if (isLoading) return; // Prevent closing while loading
        setFormData({
            component: "",
            componentVersion: "",
            componentGroup: "",
            importFileDownloadUrl: "",
            remark: ""
        });
        onClose();
    };

    // Clear validation error when input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear validation error when download URL field changes
        if (field === "importFileDownloadUrl" && validationError && onClearValidationError) {
            onClearValidationError();
        }
    };

    useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

    return (
        <div
            data-testid={`${dataTestId}-overlay`}
            className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start lg:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`}
        >
            <div
                data-testid={dataTestId}
                className={`border ${colors.borderPrimary} ${colors.bgModal} min-h-[40vh] max-h-[80vh] w-[32rem] min-w-[512px] flex-shrink-0 flex flex-col rounded-lg shadow-xl`}
            >
                {/* Header */}
                <div className={`px-6 py-6 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
                    <div className="flex items-center justify-between">
                        <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
                          {t("project.imported-components.add-import-component-title")}
                        </h2>
                    </div>
                    <p className={`${colors.textSecondary} text-sm mt-2`} data-testid={`${dataTestId}-description`}>
                        {t("project.imported-components.add-import-component-description")}
                    </p>
                </div>

                {/* Content - Scrollable */}
                <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden" noValidate data-testid={`${dataTestId}-form`}>
                    <div className="flex-1 overflow-y-auto px-6 space-y-4" data-testid={`${dataTestId}-content`}>
                        {/* Download Url */}
                        <div data-testid={fieldContainerTestId("download-url")}>
                            <label
                                className={`mb-2 block text-sm font-medium ${colors.textPrimary}`}
                                data-testid={fieldLabelTestId("download-url")}
                            >
                                {t("project.imported-components.nexus-download-url")} <span className={colors.textDanger}>*</span>
                            </label>
                            <input
                                type="url"
                                value={formData.importFileDownloadUrl}
                                onChange={(e) => handleInputChange("importFileDownloadUrl", e.target.value)}
                                placeholder={t("project.imported-components.enter-url")}
                                disabled={isLoading}
                                className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                                required
                                maxLength={CHARACTER_LENGTH.COMMENT}
                                data-testid={fieldInputTestId("download-url")}
                            />
                            {validationError && (
                                <p className={`mt-1 text-sm ${colors.textDanger}`} data-testid={fieldErrorTestId("download-url")}>
                                    {validationError}
                                </p>
                            )}
                        </div>

                        {/* Description */}
                        <div data-testid={fieldContainerTestId("description")}>
                            <label
                                className={`mb-2 block text-sm font-medium ${colors.textPrimary}`}
                                data-testid={fieldLabelTestId("description")}
                            >
                                {t("project.imported-components.description-label")}
                            </label>
                            <CustomTextarea
                                value={formData.remark}
                                onChange={(e) => handleInputChange("remark", e.target.value)}
                                placeholder={t("project.imported-components.write-description")}
                                disabled={isLoading}
                                rows={4}
                                maxLength={CHARACTER_LENGTH.COMMENT}
                                data-testid={fieldInputTestId("description")}
                            />
                            <p className={`mt-1 text-xs ${colors.textCharacterCount}`} data-testid={`${dataTestId}-field-description-char-count`}>
                                {(formData.remark || "").length}/{CHARACTER_LENGTH.COMMENT}
                            </p>
                        </div>
                    </div>

                    {/* Footer - Fixed at bottom */}
                    <div className="flex-shrink-0 py-4">
                        <div className={`h-px w-full ${colors.bgDivider} mb-4`}></div>

                        {/* Action Buttons */}
                        <div className={`${colors.borderPrimary} px-6`} data-testid={`${dataTestId}-footer`}>
                            <div className="flex items-center justify-end">
                                <div className="flex items-center space-x-3">
                                    <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleClose}>{t("common.cancel-button")}</OutlineButton>
                                    <PrimaryButton data-testid={`${dataTestId}-submit-button`} onClick={handleSubmit} disabled={!isFormValid || isLoading}>
                                        {isLoading ? (t("common.adding") || "Adding...") : t("project.imported-components.save")}
                                    </PrimaryButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddImportComponentModal; 